import React, { useState, useCallback } from "react";
import { Box, Divider, InputAdornment } from "@mui/material";
import Dropzone from "react-dropzone";
import CoreCard from "../../../app/core/components/CoreCard/CoreCard";
import CoreButtonIcon from "../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LOGO_UPLOAD_MAX_SIZE } from "../../../utils/constants";
import { doUploadLogoFileOnS3, getUploadLogoURL } from "./gestisciStudioSlice";
import { useCreateStudioMutation } from "../../api/admin/gestisciStudioApi";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SignpostIcon from "@mui/icons-material/Signpost";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LanguageIcon from "@mui/icons-material/Language";
import BadgeIcon from "@mui/icons-material/Badge";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreTextArea from "../../../app/core/components/CoreTextArea/CoreTextArea";
import { useTranslation } from "react-i18next";
import CoreIconDelete from "../../../app/core/components/CoreIconDelete/CoreIconDelete";
import CoreBack from "../../../app/core/components/CoreBack/CoreBack";

const CreaStudio = () => {
  const { t } = useTranslation();
  const baseColors = useSelector((state) => state.app.appConfig.assets.colors);
  const [formData, setFormData] = useState({
    primaryColor: null,
    secondaryColor: null,
  });
  const [isNewLogoDropped, setIsNewLogoDropped] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});

  const [createStudio, { isLoading: isCreateLoading }] =
    useCreateStudioMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    //Rimuovi l'errore dallo stato quando inizi a scrivere in un campo
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleResetColor = () => {
    setFormData((prevValue) => ({
      ...prevValue,
      primaryColor: null,
      secondaryColor: null,
    }));
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        const logoFile = file;
        setFormData((prevValue) => ({
          ...prevValue,
          logoFile: logoFile,
        }));
        const reader = new FileReader();
        reader.onload = () => {
          if (file.size > LOGO_UPLOAD_MAX_SIZE) {
            toast.warning(t("InvalidFileSize"));
          } else {
            setIsNewLogoDropped(true);
            setFormData((prevValue) => ({
              ...prevValue,
              dropLogo: reader.result,
            }));
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [t]
  );

  const uploadLogo = async () => {
    let imgUrl = "";
    await dispatch(getUploadLogoURL()).then(async (logoResponse) => {
      if (logoResponse && logoResponse.payload && logoResponse.payload.Data) {
        const s3_endpoint = logoResponse.payload.Data;
        const questionMarkIndex = s3_endpoint.indexOf("?");
        const urlBeforeQuestionMark = s3_endpoint.substring(
          0,
          questionMarkIndex
        );
        imgUrl = urlBeforeQuestionMark;
        await dispatch(
          doUploadLogoFileOnS3({
            url: s3_endpoint,
            file: new Blob([formData.logoFile]),
            type: formData.logoFile.type,
          })
        ).then(() => {
          setFormData({ ...formData, logoImageUrl: urlBeforeQuestionMark });
        });
      }
    });
    return imgUrl;
  };

  const deleteLogo = () => {
    setIsNewLogoDropped(false);
    setFormData((prevValue) => ({
      ...prevValue,
      dropLogo: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setValidationErrors({});
    const errors = {};
    if (!formData.name) {
      errors.name = t("FieldRequired");
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const id = toast.loading(t("Creation..."), {
      type: "info",
    });

    let newLogoUploaded = formData.dropLogo;
    if (isNewLogoDropped) newLogoUploaded = await uploadLogo();
    const studioData = {
      name: formData.name,
      address: formData.address,
      vat: formData.vat,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      logoImageUrl: newLogoUploaded,
      description: formData.description,
      primaryColor: formData.primaryColor,
      secondaryColor: formData.secondaryColor,
      website: formData.website,
    };

    createStudio(studioData)
      .unwrap()
      .then((res) => {
        if (res.Result !== 1) {
          toast.update(id, {
            render: res.Exception || t("ReqErr"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
          return;
        }

        toast.update(id, {
          render: `${t("StudioCreated")}`,
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
        navigate(`/auth/admin/${t("studiosRoute")}`);
      })
      .catch(() => {
        toast.update(id, {
          render: t("ReqErr"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex items-center w-full">
        <CoreBack />
        <h2 className="w-full">{t("CreateStudio")}</h2>
      </div>
      <div className="flex flex-row mx-auto w-11/12 space-x-24 justify-between">
        <div className="flex flex-col w-full justify-start  px-12 ">
          <div className="flex flex-row items-center space-x-[10%]">
            <div className="flex-row flex items-center my-12 space-x-8">
              <div className="flex">
                <CoreCard
                  className="relative"
                  width={"10rem"}
                  height={"10rem"}
                  child={
                    formData.dropLogo ? (
                      <>
                        <CoreIconDelete onDelete={deleteLogo} />
                        <img
                          alt="logo"
                          src={formData.dropLogo}
                          style={{
                            maxWidth: "100px",
                            maxHeight: "100px",
                            paddingTop: "30px",
                          }}
                        />
                      </>
                    ) : (
                      <Dropzone onDrop={onDrop}>
                        {({ getRootProps, getInputProps }) => (
                          <div
                            {...getRootProps()}
                            style={{
                              paddingTop: "20px",
                              textAlign: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input {...getInputProps()} />
                            <Box display="flex" flexDirection="column">
                              <CoreButtonIcon
                                icon={
                                  <UploadFileRoundedIcon
                                    className="text-gray"
                                    sx={{ fontSize: 70 }}
                                  />
                                }
                              />
                            </Box>
                          </div>
                        )}
                      </Dropzone>
                    )
                  }
                />
              </div>
            </div>
          </div>
          <Divider className="my-8" />
          <div className="flex flex-row justify-between space-x-12">
            <div className="flex flex-col w-1/2">
              <h3 className="font-bold m-0">{t("InsertStudioData")}</h3>
              <CoreInputField
                name="name"
                label={`${t("Name")} ${t("studio")}`}
                type="text"
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ApartmentIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="address"
                label={t("Address")}
                value={formData.address}
                onChange={handleChange}
                error={!!validationErrors.address}
                helperText={validationErrors.address}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SignpostIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="email"
                label="Email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="phoneNumber"
                label={t("Phone")}
                value={formData.phoneNumber}
                onChange={handleChange}
                error={!!validationErrors.phoneNumber}
                helperText={validationErrors.phoneNumber}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIphoneIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="website"
                label={t("Website")}
                value={formData.website}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LanguageIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="vat"
                label="Partita IVA"
                value={formData.vat}
                onChange={handleChange}
                error={!!validationErrors.vat}
                helperText={validationErrors.vat}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="flex flex-col w-1/2">
              <div className="flex flex-col w-full">
                <h3 className="font-bold m-0">{t("StudioDescription")}</h3>
                <CoreTextArea
                  name="description"
                  label={t("StudioDescription")}
                  rows={5}
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-3 mb-6"
                />
              </div>
              <h3 className="font-bold m-0">{t("YourColors")}</h3>
              <p>{t("EditYourColors")}</p>
              <div className="mt-1 flex justify-between w-full">
                <CoreInputField
                  name="primaryColor"
                  label={t("PrimaryColor")}
                  type="color"
                  value={formData.primaryColor || "#ffffff"}
                  onChange={handleChange}
                  focused
                  color="grey"
                  className="w-1/2"
                />
                <CoreInputField
                  name="secondaryColor"
                  label={t("SecondaryColor")}
                  type="color"
                  value={formData.secondaryColor || "#ffffff"}
                  onChange={handleChange}
                  focused
                  color="grey"
                  className="w-1/2"
                />
              </div>
              <div className="text-start mt-4 space-x-4">
                <CoreButton
                  testo="reset"
                  className="btn-round-outline"
                  onClick={handleResetColor}
                />
              </div>
            </div>
          </div>

          <div className="w-1/2 mt-12">
            <CoreButton
              testo={isCreateLoading ? t("Wait") : t("Create")}
              className="btn-round"
              onClick={handleSubmit}
              disabled={isCreateLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreaStudio;
